<template>
  <portal :to="portalTarget">
    <transition name="flyout-slide-fade">
      <div
        v-if="open"
        v-outside-click="{ handler: close, excludeDOM: outsideClickExcludes }"
        ref="flyout"
        :class="b({ spacing, width, scrollable })"
        :style="dynamicStyle"
      >
        <!-- Close button -->
        <button
          v-if="hasCloseIcon"
          :class="b('close-button')"
          :title="$t('c-flyout.closeButtonTitle')"
          type="button"
          @click="close"
        >
          <span class="invisible">{{ $t('c-flyout.closeButtonTitle') }}</span>
          <e-icon-sprite icon="i-close" />
        </button>

        <div :class="b('wrapper')">
          <!-- Header -->

          <template v-if="title">
            <div
              ref="title"
              :class="b('header')"
            >
              {{ title }}
            </div>
            <div :class="b('header-separator')"></div>
          </template>

          <!-- Main content -->
          <div
            ref="content"
            :class="b('content')"
          >
            <!-- @slot Content slot -->
            <slot></slot>
          </div>

          <!-- Sticky footer -->
          <div
            v-if="$slots.footer"
            ref="footer"
            :class="b('sticky-footer')"
          >
            <!-- @slot Sticky footer slot -->
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </transition>

    <!-- Backdrop -->
    <transition name="flyout-fade">
      <div
        v-if="open"
        :style="dynamicStyle"
        :class="b('backdrop')"
      ></div>
    </transition>
  </portal>
</template>

<script>
  import { Portal } from 'portal-vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import propScale from '@/helpers/prop.scale.js';

  /**
   * This component renders a flyout "modal" to display individual content pasted into it's slots.
   */
  export default {
    name: 'c-flyout',
    components: {
      eIconSprite,
      portal: Portal,
    },

    // components: {},
    // mixins: [],

    props: {
      /**
       * Describes the name of the portal-target to render the flyout.
       */
      portalTarget: {
        type: String,
        default: 'flyout-container',
      },

      /**
       * Optional title for the flyout.
       */
      title: {
        type: String,
        default: '',
      },

      /**
       * Toggles the visibility of the flyout.
       */
      open: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines if the flyout has a close icon.
       */
      hasCloseIcon: {
        type: Boolean,
        default: true,
      },

      /**
       * Gets a list of DOM elements that are excluded for the outside click handler.
       */
      outsideClickExcludes: {
        type: Array,
        default: () => [],
      },

      /**
       * Defines the inner-spacing for the content slot.
       */
      spacing: propScale(500, [0, 500]),

      /**
       * Defines the width for the flyout.
       */
      width: propScale(500, [500, 700]),

      /**
       * Defines if the flyout can be scrolled
       */
      scrollable: {
        type: Boolean,
        default: true,
      },
    },

    emits: ['close'],

    data() {
      return {
        /**
         * @type {Number} Holds the top position of the component which is the bottom value of the header.
         */
        topPosition: 0,
      };
    },

    computed: {
      /**
       * Gets the dynamic inline style values.
       *
       * @returns {Object}
       */
      dynamicStyle() {
        return {
          top: `${this.topPosition}px`,
        };
      },
    },
    watch: {
      /**
       * Observes the "open" state and calculates the top position of the component.
       *
       * @param {Boolean} isOpen - Shows if the flyout is open.
       */
      open(isOpen) {
        if (isOpen) {
          window.addEventListener('scroll', this.calcTopPosition, { passive: true });
          window.addEventListener('resizeend', this.calcTopPosition);

          this.calcTopPosition();

          if (this.scrollable) {
            setTimeout(() => {
              this.$bodyscroll.disableBodyScroll(this.$refs.content);
            });
          }
        } else {
          window.removeEventListener('scroll', this.calcTopPosition);
          window.removeEventListener('resizeend', this.calcTopPosition);

          if (this.scrollable) {
            this.$bodyscroll.enableBodyScroll(this.$refs.content);
          }
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('scroll', this.calcTopPosition);
      window.removeEventListener('resizeend', this.calcTopPosition);

      if (this.scrollable) {
        this.$bodyscroll.enableBodyScroll(this.$refs.content);
      }
    },
    // unmounted() {},

    methods: {
      /**
       * Calculates the top position of the component.
       */
      calcTopPosition() {
        const header = document.querySelector('.c-header');

        if (header) {
          const { bottom } = header.getBoundingClientRect() || {};

          this.topPosition = Math.max(bottom, 0);
        }

        // Timeout is needed to wait until the DOM elements are rendered. (this.$nexttick isn't working).
        setTimeout(() => {
          const { title, content, footer } = this.$refs;

          if (title && content && footer) {
            content.style.maxHeight = `calc(100% - ${title.clientHeight + footer.clientHeight}px)`;
          }
        });
      },

      /**
       * Close event handler.
       */
      close() {
        this.$emit('close');
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-flyout {
    $flyout--width: 665px;

    @include mixins.z-index(modal);

    position: fixed;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    width: 100vw;
    max-width: 100vw;
    border-left: 1px solid variables.$color-grayscale--500;
    background-color: variables.$color-grayscale--1000;

    @include mixins.media(md) {
      width: functions.rem($flyout--width);
    }

    @include mixins.media(lg) {
      right: calc(50% - #{math.div(map.get(variables.$breakpoints, lg), 2)});
    }

    &::after {
      // Gap filler
      @include mixins.z-index(back);

      position: absolute;
      top: 0;
      left: 100%;
      display: block;
      content: '';
      width: 50vw;
      height: 100vh;
      background: variables.$color-grayscale--1000;
    }

    &--spacing-500 {
      padding: functions.rem(variables.$spacing--20);

      @include mixins.media(md) {
        padding: functions.rem(
          variables.$spacing--80 variables.$spacing--80 variables.$spacing--40 variables.$spacing--40
        );
      }
    }

    &--width-700 {
      @include mixins.media(md) {
        width: functions.rem(700px);
      }
    }

    &__backdrop {
      @include mixins.z-index(backdrop);

      position: fixed;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(variables.$color-grayscale--100, 0.8);
      pointer-events: all;
    }

    &__header {
      @extend %heading-h3;
      @include mixins.font(variables.$font-size--24, variables.$line-height--28, variables.$font-weight--semi-bold);

      margin-bottom: 0;
      padding-right: functions.rem(variables.$spacing--30);
      padding-bottom: functions.rem(variables.$spacing--20);
      padding-left: functions.rem(variables.$spacing--15);

      @include mixins.media(md) {
        padding-bottom: functions.rem(variables.$spacing--30);
        padding-left: 0;
      }
    }

    &__header-separator {
      margin-bottom: functions.rem(variables.$spacing--10);

      @include mixins.media(md) {
        position: relative;
      }

      &::before {
        position: absolute;
        right: 0;
        left: 0;
        content: '';
        height: 1px;
        background-color: variables.$color-grayscale--900;
      }
    }

    &__wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    &__content {
      flex: 1 0 auto;
      max-height: 100%;
      padding: functions.rem(0 variables.$spacing--15);

      @include mixins.media(md) {
        padding: 0;
      }
    }

    &__sticky-footer {
      flex: 0 1 auto;
      padding: functions.rem(variables.$spacing--20 variables.$spacing--15 0);
      border-top: 2px solid variables.$color-grayscale--900;

      @include mixins.media(md) {
        padding: functions.rem(variables.$spacing--20 0 0);
        border-top: 0;
      }
    }

    &__close-button {
      @extend %button-reset;

      position: absolute;
      top: functions.rem(variables.$spacing--20);
      right: functions.rem(variables.$spacing--15);

      @include mixins.media(md) {
        top: functions.rem(variables.$spacing--80);
        right: functions.rem(variables.$spacing--80);
      }

      .e-icon-sprite {
        width: functions.rem(20px);
        height: functions.rem(20px);
      }
    }

    &--scrollable &__content {
      overflow: initial;
    }

    // Slide-fade animation
    &.flyout-slide-fade-enter-active,
    &.flyout-slide-fade-leave-active {
      transform: translateX(0);
      transition: all variables.$transition-duration--600 ease-in-out;
    }

    &.flyout-slide-fade-enter-from,
    &.flyout-slide-fade-leave-to {
      @include mixins.media($down: md) {
        opacity: 0;
        transform: translateX(100%);
      }

      @include mixins.media(lg) {
        right: -$flyout--width;
      }
    }

    // Fade animation
    &.flyout-fade-enter-active,
    &.flyout-fade-leave-active {
      opacity: 1;
      transition: all variables.$transition-duration--600 ease-in-out;
    }

    &.flyout-fade-enter-from,
    &.flyout-fade-leave-to {
      @include mixins.media($down: md) {
        opacity: 0;
      }
    }
  }
</style>
